import type { SponsoredDisplayAudienceTarget } from "@adpilot/shared";
import { IconChevronDown, IconSearch } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Field, FieldLabel } from "@/components/ui/field";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type {
  SponsoredDisplayAudienceRecommendations,
  SponsoredDisplayContextualRecommendations,
} from "@/lib/campaign-creation-api";
import { cn } from "@/lib/utils";
import {
  CandidateRow,
  CandidateWorkspace,
  EmptyCandidates,
  LoadingRows,
  TruncatedNotice,
} from "./CandidateWorkspace";

const AUDIENCE_CATEGORY_ORDER = ["lifestyle", "interest", "life_event", "in_market"] as const;

type AddAudienceTarget = (label: string, target: SponsoredDisplayAudienceTarget) => void;

export function AmazonAudienceCandidates({
  data,
  loading,
  search,
  onSearchChange,
  onAdd,
}: {
  data?: SponsoredDisplayAudienceRecommendations;
  loading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  onAdd: AddAudienceTarget;
}) {
  const { t } = useTranslation("campaignCreation");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const normalized = search.trim().toLocaleLowerCase();
  const categoriesByType = new Map(
    (data?.categories ?? []).map((category) => [category.category, category]),
  );
  const categories = AUDIENCE_CATEGORY_ORDER.map(
    (category) =>
      categoriesByType.get(category) ?? { category, items: [], truncated: false as const },
  );
  const categoryItems = [
    { value: "all", label: t("sd.audience.all") },
    ...categories.map((category) => ({
      value: category.category,
      label: t(`sd.audience.categories.${category.category}`),
    })),
  ];
  return (
    <CandidateWorkspace>
      <div className="flex flex-col gap-3 p-4">
        <Select
          items={categoryItems}
          value={categoryFilter}
          onValueChange={(value) => value && setCategoryFilter(value)}
        >
          <SelectTrigger className="max-w-64">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {categoryItems.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        <InputGroup>
          <InputGroupAddon>
            <IconSearch />
          </InputGroupAddon>
          <InputGroupInput
            aria-label={t("sd.audience.search")}
            placeholder={t("sd.audience.search")}
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
          />
        </InputGroup>
        {loading ? (
          <LoadingRows />
        ) : categories.length === 0 ? (
          <EmptyCandidates />
        ) : (
          categories
            .filter((category) => categoryFilter === "all" || category.category === categoryFilter)
            .map((category) => (
              <AudienceCategoryGroup
                category={category}
                key={category.category}
                normalizedSearch={normalized}
                onAdd={onAdd}
              />
            ))
        )}
      </div>
    </CandidateWorkspace>
  );
}

function AudienceCategoryGroup({
  category,
  normalizedSearch,
  onAdd,
}: {
  category: SponsoredDisplayAudienceRecommendations["categories"][number];
  normalizedSearch: string;
  onAdd: AddAudienceTarget;
}) {
  const { t } = useTranslation("campaignCreation");
  const [open, setOpen] = useState(category.category === "lifestyle");
  const items = category.items.filter(
    (item) => !normalizedSearch || item.name.toLocaleLowerCase().includes(normalizedSearch),
  );
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger
        className="flex w-full items-center gap-2 py-2 text-left text-sm font-semibold"
        data-category={category.category}
        data-slot="sd-audience-category-trigger"
      >
        <IconChevronDown
          className={cn("transition-transform", !open && "-rotate-90")}
          data-icon="inline-start"
        />
        {t(`sd.audience.categories.${category.category}`)}
      </CollapsibleTrigger>
      <CollapsiblePanel>
        <div className="flex flex-col gap-2 pb-3">
          {items.map((item) => (
            <CandidateRow
              key={item.audienceId}
              label={item.name}
              onAdd={() =>
                onAdd(item.name, {
                  type: "amazon_audience",
                  audienceId: item.audienceId,
                  bid: null,
                })
              }
            />
          ))}
          {category.truncated ? <TruncatedNotice /> : null}
        </div>
      </CollapsiblePanel>
    </Collapsible>
  );
}

export function RemarketingCandidates({
  categories,
  mode,
  onAdd,
}: {
  categories: SponsoredDisplayContextualRecommendations["categories"]["items"];
  mode: "views" | "purchases";
  onAdd: AddAudienceTarget;
}) {
  const { t } = useTranslation("campaignCreation");
  const [lookback, setLookback] = useState(30);
  const [tab, setTab] = useState("suggestions");
  const [search, setSearch] = useState("");
  const rows =
    mode === "views"
      ? (["exact_product", "similar_product"] as const)
      : (["exact_product", "related_product"] as const);
  const allowedLookbacks = mode === "views" ? [7, 14, 30, 60, 90] : [7, 14, 30, 60, 90, 180, 365];

  function addCategory(category: (typeof categories)[number]) {
    const source = {
      type: "category" as const,
      categoryId: category.categoryId,
      refinements: {
        brandId: null,
        price: null,
        reviewRating: null,
        primeShippingOnly: false,
      },
    };
    if (mode === "views") {
      onAdd(category.name, {
        type: "views_remarketing",
        source,
        lookbackDays: lookback as 7 | 14 | 30 | 60 | 90,
        bid: null,
      });
    } else {
      onAdd(category.name, {
        type: "purchases_remarketing",
        source,
        lookbackDays: lookback as 7 | 14 | 30 | 60 | 90 | 180 | 365,
        bid: null,
      });
    }
  }

  return (
    <CandidateWorkspace>
      <div className="flex flex-col gap-4 p-4">
        <p className="text-sm text-muted-foreground">{t(`sd.audience.${mode}.description`)}</p>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="suggestions">{t("sd.audience.suggestions")}</TabsTrigger>
            <TabsTrigger value="search">{t("sd.audience.searchTab")}</TabsTrigger>
          </TabsList>
          <TabsContent value="suggestions" className="flex flex-col gap-4 pt-3">
            <Field className="max-w-52">
              <FieldLabel>{t("sd.audience.lookback")}</FieldLabel>
              <Select
                items={allowedLookbacks.map((days) => ({
                  value: String(days),
                  label: t("sd.audience.days", { days }),
                }))}
                value={String(lookback)}
                onValueChange={(value) => value && setLookback(Number(value))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {allowedLookbacks.map((days) => (
                      <SelectItem key={days} value={String(days)}>
                        {t("sd.audience.days", { days })}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </Field>
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("sd.audience.dynamicSegments")}
            </p>
            {rows.map((source) => (
              <CandidateRow
                key={source}
                label={t(`sd.audience.sources.${source}`)}
                onAdd={() => {
                  const label = t(`sd.audience.sources.${source}`);
                  if (mode === "views") {
                    onAdd(label, {
                      type: "views_remarketing",
                      source: { type: source as "exact_product" | "similar_product" },
                      lookbackDays: lookback as 7 | 14 | 30 | 60 | 90,
                      bid: null,
                    });
                  } else {
                    onAdd(label, {
                      type: "purchases_remarketing",
                      source: { type: source as "exact_product" | "related_product" },
                      lookbackDays: lookback as 7 | 14 | 30 | 60 | 90 | 180 | 365,
                      bid: null,
                    });
                  }
                }}
              />
            ))}
            <p className="pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("sd.contextual.productCategories")}
            </p>
            {categories.length === 0 ? (
              <EmptyCandidates />
            ) : (
              categories.map((category) => (
                <CandidateRow
                  key={category.categoryId}
                  label={category.name}
                  description={category.path.join(" › ")}
                  onAdd={() => addCategory(category)}
                />
              ))
            )}
          </TabsContent>
          <TabsContent value="search" className="flex flex-col gap-3 pt-3">
            <InputGroup>
              <InputGroupAddon>
                <IconSearch />
              </InputGroupAddon>
              <InputGroupInput
                aria-label={t("sd.audience.search")}
                placeholder={t("sd.audience.search")}
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </InputGroup>
            {categories
              .filter((category) =>
                category.name.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()),
              )
              .map((category) => (
                <CandidateRow
                  key={category.categoryId}
                  label={category.name}
                  description={category.path.join(" › ")}
                  onAdd={() => addCategory(category)}
                />
              ))}
            {categories.length === 0 ? <EmptyCandidates /> : null}
          </TabsContent>
        </Tabs>
      </div>
    </CandidateWorkspace>
  );
}
